import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { Link, Text } from '@/components/common'
import routes from '@/routes'

import styles from './HowItWorks.module.scss'

const slideLinks = [routes.trackReceipts, routes.taxPrep, routes.neatFeatures]

function RevealImage({ alt, src }) {
  const imageRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const imageElement = imageRef.current
    if (!imageElement || !('IntersectionObserver' in window)) {
      setIsVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.2 },
    )

    observer.observe(imageElement)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={imageRef}
      className={cx(styles.imagePanel, isVisible && styles.imageVisible)}
    >
      <Image alt={alt} className={styles.image} height={1024} src={src} width={1536} />
    </div>
  )
}

RevealImage.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
}

export default function HowItWorks({ t }) {
  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Text className={styles.label}>{t.label}</Text>
        {t.slides.map((slide, index) => (
          <article key={slide.title} className={styles.step}>
            <RevealImage alt={slide.title} src={slide.image} />
            <div className={styles.content}>
              <Text.H2 className={styles.title}>{slide.title}</Text.H2>
              <Text.P className={styles.description}>{slide.description}</Text.P>
              <ul className={styles.bullets}>
                {slide.bullets.map((item) => (
                  <li key={item} className={styles.bulletItem}>
                    <Image alt="" className={styles.checkIcon} height={20} src="/home2/green-check.png" width={20} />
                    <Text>{item}</Text>
                  </li>
                ))}
              </ul>
              <Link className={styles.cta} size="xlTall" to={slideLinks[index]} variant="success">{slide.cta}</Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

HowItWorks.propTypes = {
  t: PropTypes.shape({
    label: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    ).isRequired,
  }).isRequired,
}
